import { useMemo } from 'react';
import { T, Num, Var, useGT } from 'gt-react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  CurrencyDollarIcon,
  ArrowUUpLeftIcon,
  EyeIcon,
  EyeSlashIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import type { Charge } from '@erp/rpc';
import {
  useChargesWithReversals,
  type ChargeWithReversalInfo,
} from '@/hooks/useChargesWithReversals';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ChargesTableProps {
  charges: Charge[];
  currency?: string;
  title?: string;
  emptyMessage?: string;
  className?: string;
  /** Show a compact version without the table header */
  compact?: boolean;
}

function formatCurrency(
  value: string | number | null | undefined,
  currency = 'USD',
) {
  if (value === null || value === undefined) return '—';
  const numeric = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numeric)) return '—';

  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
}

/**
 * Reusable table component for displaying charges.
 * Can be used for header charges, line charges, or any list of charges.
 *
 * Features:
 * - Fully reversed charges and $0.00 charges are hidden by default
 * - Partially reversed charges show remaining amount
 * - Toggle button to show/hide reversed and zero charges
 * - Visual indicators for charges with reversals
 */
export function ChargesTable({
  charges,
  currency = 'USD',
  title,
  emptyMessage: emptyMessageProp,
  className,
  compact = false,
}: ChargesTableProps) {
  const gt = useGT();
  const emptyMessage =
    emptyMessageProp ??
    gt('No charges', { context: 'empty state when order has no charges' });
  const {
    visibleCharges,
    total,
    showReversed,
    toggleShowReversed,
    hiddenReversedCount,
  } = useChargesWithReversals(charges);

  const columns = useMemo<ColumnDef<ChargeWithReversalInfo>[]>(
    () => [
      {
        id: 'icon',
        header: '',
        cell: (info) => {
          const charge = info.row.original;
          return (
            <div className="text-center">
              {charge.isReversal ? (
                <ArrowUUpLeftIcon className="size-4 text-amber-600 inline" />
              ) : charge.isPartiallyReversed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <WarningCircleIcon className="size-4 text-orange-500 inline cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      <T context="charge partially reversed tooltip title">
                        Partially reversed
                      </T>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      <T context="charge reversal amount detail">
                        <Var>
                          {formatCurrency(charge.reversedAmount, currency)}
                        </Var>{' '}
                        of <Var>{formatCurrency(charge.amount, currency)}</Var>{' '}
                        reversed
                      </T>
                    </p>
                  </TooltipContent>
                </Tooltip>
              ) : charge.isFullyReversed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ArrowUUpLeftIcon className="size-4 text-amber-600 inline cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <T context="charge fully reversed tooltip">
                      Fully reversed
                    </T>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <CurrencyDollarIcon className="size-4 text-muted-foreground inline" />
              )}
            </div>
          );
        },
        size: 40,
      },
      {
        id: 'category',
        header: gt('Category', { context: 'charges table column' }),
        cell: (info) => {
          const charge = info.row.original;
          const categoryLabel =
            charge.chargeCategory?.label ||
            charge.chargeCategory?.value ||
            gt('Charge', { context: 'fallback charge category label' });

          return (
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'font-medium',
                  charge.isReversal && 'text-amber-700',
                  charge.isFullyReversed && 'text-amber-700 line-through',
                )}
              >
                {categoryLabel}
              </span>
              {charge.isReversal && (
                <Badge
                  variant="outline"
                  className="text-[10px] py-0.5 px-1.5 border-amber-500 text-amber-700 bg-amber-50"
                >
                  <T context="badge charge is a reversal">Reversal</T>
                </Badge>
              )}
              {charge.isPartiallyReversed && (
                <Badge
                  variant="outline"
                  className="text-[10px] py-0.5 px-1.5 border-orange-400 text-orange-600 bg-orange-50"
                >
                  <T context="badge charge partially reversed">
                    Partial Reversal
                  </T>
                </Badge>
              )}
              {charge.isFullyReversed && (
                <Badge
                  variant="outline"
                  className="text-[10px] py-0.5 px-1.5 border-amber-500 text-amber-700 bg-amber-50"
                >
                  <T context="badge charge fully reversed">Reversed</T>
                </Badge>
              )}
              {charge.isEstimate && (
                <Badge
                  variant="secondary"
                  className="text-[10px] py-0.5 px-1.5"
                >
                  <T context="badge estimated charge">EST</T>
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        id: 'direction',
        header: () => (
          <div className="text-center">
            {gt('Type', { context: 'charges table column CREDIT vs DEBIT' })}
          </div>
        ),
        cell: (info) => {
          const charge = info.row.original;
          const isCredit = charge.direction === 'CREDIT';
          return (
            <div className="text-center">
              <Badge
                variant={isCredit ? 'secondary' : 'outline'}
                className={cn(
                  'text-[10px] py-0.5 px-1.5',
                  charge.isFullyReversed && 'opacity-50',
                )}
              >
                {charge.direction}
              </Badge>
            </div>
          );
        },
        size: 80,
      },
      {
        id: 'amount',
        header: () => (
          <div className="text-right">
            {gt('Amount', { context: 'charges table column' })}
          </div>
        ),
        cell: (info) => {
          const charge = info.row.original;
          const isCredit = charge.direction === 'CREDIT';

          // For partially reversed charges not in "show reversed" mode,
          // show remaining amount with original strikethrough
          const showRemaining = charge.isPartiallyReversed && !showReversed;

          const displayAmount = showRemaining
            ? charge.remainingAmount
            : Number(charge.amount);

          let amountFormatted = formatCurrency(
            displayAmount,
            charge.currencyCode || currency,
          );

          // Show CREDIT charges in financial negative notation (parentheses)
          if (isCredit) {
            amountFormatted = `(${amountFormatted})`;
          }

          return (
            <div
              className={cn(
                'text-right font-semibold',
                charge.isReversal && 'text-amber-700',
                charge.isFullyReversed && 'text-amber-700',
              )}
            >
              {showRemaining && (
                <span className="text-xs text-muted-foreground line-through mr-2">
                  {formatCurrency(
                    charge.amount,
                    charge.currencyCode || currency,
                  )}
                </span>
              )}
              <span className={cn(charge.isFullyReversed && 'line-through')}>
                {amountFormatted}
              </span>
            </div>
          );
        },
        size: 140,
      },
    ],
    [currency, showReversed, gt],
  );

  const table = useReactTable({
    data: visibleCharges,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  // Show empty state if no charges at all
  if (charges.length === 0) {
    return (
      <div className={cn('text-sm text-muted-foreground py-2', className)}>
        {emptyMessage}
      </div>
    );
  }

  // If all charges are hidden (fully reversed), show a different message
  if (visibleCharges.length === 0 && !showReversed) {
    return (
      <div className={cn('space-y-2', className)}>
        {title && (
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <CurrencyDollarIcon className="size-4" />
            <T context="charges section title with count">
              <Var>{title}</Var> (<Num>{charges.length}</Num>)
            </T>
          </h3>
        )}
        <div className="flex items-center justify-between text-sm text-muted-foreground py-2 px-3 border rounded-md bg-muted/30">
          <span>
            <T context="all charges hidden message">
              All charges are reversed or zero
            </T>
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleShowReversed}
            className="h-7 text-xs"
          >
            <EyeIcon className="size-3 mr-1" />
            <T context="button show reversed charges">
              Show reversed and zero (<Num>{hiddenReversedCount}</Num>)
            </T>
          </Button>
        </div>
      </div>
    );
  }

  const totalFormatted = formatCurrency(Math.abs(total), currency);
  const totalDisplay = total < 0 ? `(${totalFormatted})` : totalFormatted;

  return (
    <div className={cn('space-y-2', className)}>
      {title && (
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <CurrencyDollarIcon className="size-4" />
            <T context="charges section title with visible count">
              <Var>{title}</Var> (<Num>{visibleCharges.length}</Num>)
            </T>
          </h3>
          {hiddenReversedCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleShowReversed}
              className="h-7 text-xs"
            >
              {showReversed ? (
                <>
                  <EyeSlashIcon className="size-3 mr-1" />
                  <T context="button hide reversed charges">
                    Hide reversed and zero
                  </T>
                </>
              ) : (
                <>
                  <EyeIcon className="size-3 mr-1" />
                  <T context="button show reversed charges with count">
                    Show reversed and zero (<Num>{hiddenReversedCount}</Num>)
                  </T>
                </>
              )}
            </Button>
          )}
        </div>
      )}
      {/* Show toggle even without title if there are hidden charges */}
      {!title && hiddenReversedCount > 0 && (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleShowReversed}
            className="h-7 text-xs"
          >
            {showReversed ? (
              <>
                <EyeSlashIcon className="size-3 mr-1" />
                <T context="button hide reversed charges no title">
                  Hide reversed and zero
                </T>
              </>
            ) : (
              <>
                <EyeIcon className="size-3 mr-1" />
                <T context="button show reversed charges with count no title">
                  Show reversed and zero (<Num>{hiddenReversedCount}</Num>)
                </T>
              </>
            )}
          </Button>
        </div>
      )}
      <div className="border rounded-md overflow-hidden">
        <Table>
          {!compact && (
            <TableHeader className="bg-muted/50">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      style={{
                        width:
                          header.getSize() !== 150
                            ? `${header.getSize()}px`
                            : undefined,
                      }}
                      className="py-2"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
          )}
          <TableBody>
            {table.getRowModel().rows.map((row) => {
              const charge = row.original;
              return (
                <TableRow
                  key={row.id}
                  className={cn(
                    charge.isFullyReversed && 'bg-amber-50/30',
                    charge.isReversal && 'bg-amber-50/30',
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-2">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
            {/* Total row */}
            {visibleCharges.length > 1 && (
              <TableRow className="bg-muted/30 font-semibold">
                <TableCell colSpan={3} className="py-2 text-right">
                  <T context="charges table total row label">Total</T>
                </TableCell>
                <TableCell
                  className={cn('py-2 text-right', total < 0 && 'text-red-600')}
                >
                  {totalDisplay}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
